import React, { useState, useEffect } from 'react';
import { Button, Tag } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import { useMerchantTags, useClientCountByTags } from '../../../../../hooks/query/useEvents';
import TagSelectionModal from './TagSelectionModal';
import './AudienceTagSelector.scss';

/**
 * AudienceTagSelector component for selecting tags to define event audience
 * Uses a modal dialog for tag selection (matches Figma design)
 */
const AudienceTagSelector = ({ selectedTagIds, onTagsChange, onCountChange }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch merchant tags from /merchants/tags endpoint
  const { data: tagsData, isLoading: isLoadingTags } = useMerchantTags();

  // Always use client search to get count by tagIds (for both create and edit modes)
  // This ensures the count updates in real-time as tags are modified
  const { data: clientCountData } = useClientCountByTags({
    tagIds: selectedTagIds,
    useQueryOptions: {
      enabled: selectedTagIds.length > 0,
    },
  });

  const tags = tagsData?.results || [];

  // Update audience count when data changes
  useEffect(() => {
    if (!onCountChange) return;

    if (selectedTagIds.length === 0) {
      onCountChange(0);
      return;
    }

    // Use the client count from search endpoint for real-time preview
    if (clientCountData?.total !== undefined) {
      onCountChange(clientCountData.total);
    }
  }, [selectedTagIds, clientCountData, onCountChange]);

  // Get tag details by ID
  const getTagById = tagId => tags.find(t => t.id === tagId);

  const handleRemoveTag = tagId => {
    onTagsChange(selectedTagIds.filter(id => id !== tagId));
  };

  const handleModalConfirm = newSelectedIds => {
    onTagsChange(newSelectedIds);
  };

  // Get selected tag objects for display
  const selectedTags = selectedTagIds.map(id => getTagById(id)).filter(Boolean);

  return (
    <div className="audience-tag-selector">
      {/* Selected Tags Display */}
      <div className="audience-tag-selector__tags">
        {selectedTags.map(tag => (
          <Tag key={tag.id} closable onClose={() => handleRemoveTag(tag.id)} className="audience-tag-selector__tag">
            {tag.name}
          </Tag>
        ))}
      </div>

      {/* Choose Tags Button */}
      <div className="audience-tag-selector__actions">
        <Button
          icon={<FontAwesomeIcon icon={['fal', 'tags']} />}
          onClick={() => setIsModalOpen(true)}
          loading={isLoadingTags}
        >
          Choose Tags
        </Button>

        <a
          href="https://clientbook.zendesk.com/hc/en-us/articles/31843227439387-How-to-Upload-Tags-to-Client-Profiles"
          className="audience-tag-selector__help"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FontAwesomeIcon icon={['fal', 'question-circle']} className="m-right-4" />
          Need help defining an audience?
        </a>
      </div>

      {/* Tag Selection Modal */}
      <TagSelectionModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleModalConfirm}
        tags={tags}
        selectedTagIds={selectedTagIds}
        isLoading={isLoadingTags}
      />
    </div>
  );
};

AudienceTagSelector.propTypes = {
  selectedTagIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onTagsChange: PropTypes.func.isRequired,
  onCountChange: PropTypes.func,
};

AudienceTagSelector.defaultProps = {
  selectedTagIds: [],
  onCountChange: null,
};

export default AudienceTagSelector;
